/* Sep-14-2026 — ISBN Bank threshold amber banner.
 * 2026-02 · Warnings toggle — When admin silences ISBN warnings
 * (via the "Silence" button on this banner or the toggle in Admin →
 * ISBN Manager), the backend flips `disabled=true` and we hide
 * the alert surfaces entirely. Rationale: Bowker doesn't expose a
 * purchase API, so between blocks the amber nudge becomes noise
 * rather than a signal.
 *
 * Polls /api/admin/isbn/bank/threshold once at mount and every 5 min.
 * Surfaces:
 *   • red banner when 0 ISBNs remain in the Bowker block (blocks
 *     further releases until a new block is pasted in)
 *   • amber warning when fewer than the configured threshold (3)
 *     remain — nudge ops to order the next Bowker block
 *   • silent (green chip) when we've got healthy inventory
 *   • hidden entirely when the admin has silenced warnings
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Hash, Loader2, BellOff, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';

export default function IsbnBankThresholdBanner() {
  const [s, setS] = useState({ loading: true });

  const check = async () => {
    try {
      const r = await adminApi.isbnBankThreshold();
      setS({ loading: false, ...r });
    } catch (e) {
      setS({ loading: false, alert: false });
    }
  };
  useEffect(() => {
    check();
    const t = window.setInterval(check, 5 * 60_000);
    return () => window.clearInterval(t);
  }, []);

  const setWarnings = async (enabled) => {
    try {
      await adminApi.isbnBankWarningsSet(enabled);
      toast.success(enabled
        ? 'ISBN bank warnings re-enabled.'
        : 'ISBN bank warnings silenced.');
      await check();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update setting.');
    }
  };

  if (s.loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-[11.5px] text-muted-foreground"
           data-testid="isbn-bank-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking ISBN bank inventory…
      </div>
    );
  }
  /* Silenced — show a tiny muted chip with a one-click "re-enable"
   * so admins never lose a way back. */
  if (s.disabled) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[10.5px] text-muted-foreground"
           data-testid="isbn-bank-silenced"
           title="ISBN bank warnings are silenced. Click to re-enable.">
        <BellOff className="h-3 w-3" />
        ISBN warnings silenced
        <button type="button" onClick={() => setWarnings(true)}
                data-testid="isbn-bank-unsilence"
                className="ml-1 underline decoration-dotted underline-offset-2 hover:text-foreground">
          re-enable
        </button>
      </div>
    );
  }
  if (!s.alert) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:text-emerald-400"
           data-testid="isbn-bank-ok"
           title={`${s.available} ISBNs available in the Bowker block`}>
        <Hash className="h-3 w-3" />
        ISBN Bank · {s.available} available
        <button type="button" onClick={() => setWarnings(false)}
                data-testid="isbn-bank-silence-ok"
                title="Silence ISBN bank warnings until a real API exists"
                className="ml-1 text-muted-foreground hover:text-foreground">
          <BellOff className="h-3 w-3" />
        </button>
      </div>
    );
  }
  const critical = s.severity === 'critical';
  const cls = critical
    ? 'border-red-500/60 bg-red-500/10 text-red-900 dark:text-red-200'
    : 'border-amber-500/60 bg-amber-500/10 text-amber-900 dark:text-amber-200';
  return (
    <div className={`rounded-md border px-3 py-2 text-[12px] ${cls}`}
         data-testid="isbn-bank-warn">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        <span className="flex-1">
          {critical
            ? 'ISBN Bank is empty — no more titles can be released'
            : `ISBN Bank low · only ${s.available} of your Bowker block remain`}
        </span>
        <button type="button" onClick={() => setWarnings(false)}
                data-testid="isbn-bank-silence"
                title="Silence these warnings until we have an ISBN purchase API"
                className="inline-flex items-center gap-1 rounded-sm border border-current/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-80 hover:opacity-100">
          <BellOff className="h-3 w-3" />
          Silence
        </button>
      </div>
      <div className="mt-0.5 pl-5 text-[11px] opacity-90">
        Order the next block at{' '}
        <a href="https://www.myidentifiers.com" target="_blank" rel="noreferrer"
           className="underline decoration-dotted underline-offset-2 hover:decoration-solid">
          myidentifiers.com
        </a>{' '}
        and paste it into{' '}
        <Link to="/admin/isbn" data-testid="isbn-bank-fix-link"
              className="font-semibold underline decoration-current hover:decoration-solid">
          Admin → ISBN Manager
        </Link>.{' '}
        <button type="button" onClick={check}
                data-testid="isbn-bank-recheck"
                className="underline decoration-dotted underline-offset-2 hover:decoration-solid">
          re-check now
        </button>
      </div>
    </div>
  );
}
